import { Alert, Button, Checkbox, Form, Input } from 'antd'
import { RuleObject } from 'antd/lib/form';
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components';
import TermsOfUse from './components/TermsOfUse';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useValidateChoosingAtLeastOneAlertType } from './components/useValidateChoosingAtLeastOneAlertType';
import { Trans, useTranslation } from 'react-i18next';

const CaptchaAndButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
`;

const SendButtonContainer = styled(Button)`
    @media screen and (max-width: 600px) {
        width: 100%;
    }
`;

const RecaptchaClauseContainer = styled.div`
    font-size: 80%;

    padding-top: 0.5em;

    & a {
        font-size: 100% !important;
    }
`;

interface IProps {
    showAdditionalInfo?: boolean
}

export default function OrderForm(props: IProps): ReactElement {
    const [hasErrors, setHasErrors] = useState(true);
    const [form] = Form.useForm();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [email, setEmail] = useState();
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [naturalForestAlerts, setNaturalForestAlerts] = useState(true);
    const [observationAlerts, setObservationAlerts] = useState(true);
    const [emailSentOk, setEmailSentOk] = useState<undefined | boolean>(undefined);
    const [maintenanceBreak, setMaintenanceBreak] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation("emailAlerts");

    const validateMessages = {
        required: t("sähköpostiTarvitaan"),
        types: {
            email: t("sähköpostiTarvitaan"),
        }
    };

    const handleFieldsChange = (s: any, state: any[]) => {
        const hasAnyErrors = state.some((val: any) => val.errors.length > 0);

        setHasErrors(hasAnyErrors);
    };

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }

        return await executeRecaptcha('orderForm');
        // Do whatever you want with the token
    }, [executeRecaptcha]);

    // You can use useEffect to trigger the verification as soon as the component being loaded
    useEffect(() => {
        if (executeRecaptcha) {
            handleReCaptchaVerify();
        }
    }, [handleReCaptchaVerify, executeRecaptcha]);

    const sendOrder = async () => {
        const token = await handleReCaptchaVerify();

        try {
            setIsLoading(true);

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/AddEmailToTable`, {
                body: JSON.stringify({
                    captcha: token,
                    email,
                    termsAccepted,
                    naturalForestAlerts,
                    observationAlerts
                }),
                method: "POST"
            });

            if (res.ok) {
                setEmailSentOk(true);
            }
            else {
                if ((await res.text()) === "HUOLTOKATKO") {
                    setMaintenanceBreak(true);
                }
                setEmailSentOk(false);
            }
        } catch (error) {
            setEmailSentOk(false);
        }
        finally {
            setIsLoading(false);
        }
    };

    const validation = (rule: RuleObject, value: any, callback: (error?: string) => void) => {
        if (termsAccepted) {
            return Promise.resolve()
        }

        return Promise.reject(t("hyväksyEhdot"));
    };

    const chooseAtLeastOneAlertTypeValidation = useValidateChoosingAtLeastOneAlertType(naturalForestAlerts, observationAlerts, form);

    const noHotmailsValidator = (rule: RuleObject, value: any, callback: (error?: string) => void) => {
        const emailInput: string = value;

        if (emailInput === undefined) {
            return Promise.resolve();
        }

        if (emailInput.includes("hotmail")) {
            return Promise.reject(t("eiTuettuOsoite", { address: "Hotmail" }));
        }
        else if (emailInput.includes("live.com")) {
            return Promise.reject(t("eiTuettuOsoite", { address: "Live" }));
        }
        else if (emailInput.includes("msn.com")) {
            return Promise.reject(t("eiTuettuOsoite", { address: "Msn" }));
        }
        else if (emailInput.includes("passport.com")) {
            return Promise.reject(t("eiTuettuOsoite", { address: "Passport" }));
        }
        else if (emailInput.includes("passport.net")) {
            return Promise.reject(t("eiTuettuOsoite", { address: "Passport" }));
        }

        return Promise.resolve();
    };

    return (
        <div>
            <span>
                <Trans i18nKey={"emailAlerts:leipäteksti"}>
                    Tällä lomakkeella voit tilata sähköpostiisi ilmoituksen, kun uusi metsänkäyttöilmoitus osuu luonnonmetsään tai lajihavaintoon. Metsänkäyttöilmoitukset tarkistetaan kahden tunnin välein aina edellisen kahden tunnin ajalta.
                </Trans>
            </span>

            <div>
                {emailSentOk === false && <div>
                    <Alert type="error" message={t("virheTuli")} />
                </div>}

                {emailSentOk === false &&
                    maintenanceBreak === true && <div>
                        <Alert type="warning" message={t("huoltokatko")} />
                    </div>}

                {emailSentOk === true && <div>
                    <Alert type="success" message={t("okViesti")} />
                </div>}

                <Form form={form} validateMessages={validateMessages} onFinish={sendOrder} onFieldsChange={handleFieldsChange}>
                    <Form.Item
                        name={['email']}
                        label={t("kenttäSähköposti")}
                        rules={[
                            {
                                validator: noHotmailsValidator,
                            },
                            {
                                type: 'email'
                            },
                            {
                                required: true
                            }
                        ]}
                    >
                        <Input size={"large"}
                            onChange={(val: any) => setEmail(val.currentTarget.value)} />
                    </Form.Item>

                    <Form.Item
                        name={"naturalForests"}
                        label={t("kenttäLuonnonmetsät")}
                        validateFirst={false}
                        rules={[
                            {
                                validator: chooseAtLeastOneAlertTypeValidation,
                            }
                        ]}>
                        <Checkbox checked={naturalForestAlerts} indeterminate={false} defaultChecked={true}
                            onChange={() => setNaturalForestAlerts(!naturalForestAlerts)}></Checkbox>
                    </Form.Item>

                    <Form.Item
                        name={"observations"}
                        label={t("kenttäLajihavainnot")}
                        validateFirst={false}
                        rules={[
                            {
                                validator: chooseAtLeastOneAlertTypeValidation,
                            }
                        ]}>
                        <Checkbox checked={observationAlerts} indeterminate={false} defaultChecked={true}
                            onChange={() => setObservationAlerts(!observationAlerts)}></Checkbox>
                    </Form.Item>

                    <TermsOfUse />

                    <br />
                    <Form.Item
                        name={['acceptTerms']}
                        required={true}
                        rules={[{ validator: validation }]}
                        label={t("kenttäHyväksyEhdot")}
                    >
                        <Checkbox checked={termsAccepted} indeterminate={false} defaultChecked={false}
                            onChange={() => setTermsAccepted(!termsAccepted)}></Checkbox>
                    </Form.Item>

                    <CaptchaAndButtonsContainer>
                        <SendButtonContainer loading={isLoading} type="primary" htmlType="submit" value="Lähetä" disabled={hasErrors}>{t("nappula")}</SendButtonContainer>
                    </CaptchaAndButtonsContainer>
                </Form>
                <RecaptchaClauseContainer>
                    <Trans i18nKey={"emailAlerts:captcha"}>
                        Tätä lomaketta suojaa reCAPTCHA, johon sovelletaan Googlen <a href="https://policies.google.com/privacy">tietosuojakäytäntöjä</a> ja <a href="https://policies.google.com/terms">palveluehtoja</a>.
                    </Trans>
                </RecaptchaClauseContainer>
            </div>
        </div >
    )
}
